import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import Pagination from '../../components/ui/Pagination';
import { ChevronDownIcon } from '../../components/ui/Icons';
import { EmptyState } from '../../components/ui/PanelCard';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

export default function BanLogsTab() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ban/logs?page=${page}&limit=25`);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-gray-500 tabular-nums">{total} total ban events</p>
        <button onClick={fetchLogs} className="btn-secondary text-sm py-2">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
            <path d="M21 12a9 9 0 11-2.6-6.4M21 3v6h-6" />
          </svg>
          Refresh
        </button>
      </div>

      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-12 flex justify-center"><LoadingSpinner /></div>
        ) : logs.length === 0 ? (
          <EmptyState
            icon={<><circle cx="12" cy="12" r="9" /><path d="M5.6 5.6l12.8 12.8" /></>}
            title="No ban logs found."
          />
        ) : (
          <div className="divide-y divide-white/[0.05]">
            {logs.map(log => (
              <div key={log.id} className="p-4 hover:bg-white/[0.02] transition-colors">
                <div
                  className="flex items-start justify-between gap-4 cursor-pointer"
                  onClick={() => setExpanded(expanded === log.id ? null : log.id)}
                >
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className="text-[11px] px-2.5 py-1 rounded-full border font-semibold
                                     whitespace-nowrap flex-shrink-0
                                     bg-red-500/12 text-red-300 border-red-500/25">
                      BAN
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm text-gray-300">
                          {log.username
                            ? <span className="font-medium text-white">{log.username}</span>
                            : <span className="text-gray-600 italic">unknown user</span>
                          }
                        </p>
                        {log.username && (
                          <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5
                                            rounded border ${
                            log.is_banned
                              ? 'bg-red-500/12 text-red-300 border-red-500/25'
                              : 'bg-white/[0.05] text-gray-500 border-white/[0.08]'
                          }`}>
                            {log.is_banned ? 'banned' : 'unbanned'}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1 truncate">{log.reason}</p>
                      <p className="text-xs text-gray-600 mt-1 font-mono">
                        {log.created_at?.slice(0, 16).replace('T', ' ')}
                        <span className="mx-1.5 text-gray-700">·</span>
                        IP: {log.ip_address || 'unknown'}
                        <span className="mx-1.5 text-gray-700">·</span>
                        HWID: {log.hwid?.slice(0, 16)}…
                      </p>
                    </div>
                  </div>
                  <ChevronDownIcon className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform duration-300 ${expanded === log.id ? 'rotate-180' : ''}`} />
                </div>

                {expanded === log.id && (
                  <div className="mt-3 surface-sunken p-3.5 space-y-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Full HWID
                      </p>
                      <p className="text-xs text-gray-300 font-mono break-all">{log.hwid}</p>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Reason
                      </p>
                      <p className="text-xs text-gray-300 leading-relaxed">{log.reason}</p>
                    </div>
                    {log.user_agent && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">
                          User Agent
                        </p>
                        <p className="text-xs text-gray-300 break-all font-mono">{log.user_agent}</p>
                      </div>
                    )}
                    {log.processes?.length > 0 && (
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-600 mb-1">
                          Running processes ({log.processes.length})
                        </p>
                        <div className="max-h-40 overflow-y-auto rounded-md bg-black/25 p-2.5">
                          <pre className="text-xs text-gray-400 font-mono whitespace-pre-wrap break-all leading-relaxed">
                            {log.processes.join('\n')}
                          </pre>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Pagination page={page} totalPages={Math.ceil(total / 25)} total={total} label="ban events" onPage={setPage} />
    </div>
  );
}
